import { useState } from 'react';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { Select } from '@siliconx/shared/ui/Select';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import {
  duplicateFields,
  FIELD_OPTIONS,
  fieldLabel,
  sampleValues,
  type MappedField,
  type MappingColumn,
  type ReviewPayload,
} from '../../lib/importReview';

/**
 * SIL-1617 - step 1 of the import review page: one row per source column with
 * sample values, the proposed field and its confidence. Confirming stages and
 * validates the rows (POST mapping/confirm). Admins edit; members see it read-only.
 */
export function MappingStep({
  mapping,
  canEdit,
  busy,
  onConfirm,
}: {
  mapping: NonNullable<ReviewPayload['mapping']>;
  canEdit: boolean;
  busy: boolean;
  onConfirm: (columns: MappingColumn[]) => void;
}) {
  const [columns, setColumns] = useState<MappingColumn[]>(mapping.proposal.columns);
  const dupes = duplicateFields(columns);

  const update = (i: number, patch: Partial<MappingColumn>) =>
    setColumns((cs) => cs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));

  return (
    <Card as="section" aria-label="Column mapping" className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-foreground">Match your columns</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {mapping.reusedFromBatchId
            ? 'We reused the mapping from your last upload with these columns. '
            : 'We matched your columns to SiliconX fields. '}
          Check each one, then confirm. Nothing is imported until you do.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead>
            <tr className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <th className="py-1.5 pr-4">Your column</th>
              <th className="py-1.5 pr-4">Sample values</th>
              <th className="py-1.5 pr-4">SiliconX field</th>
              <th className="py-1.5">Match</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {columns.map((c, i) => (
              <tr key={`${c.source}-${i}`} data-testid="mapping-row">
                <td className="py-2 pr-4 font-mono text-xs text-foreground">{c.source}</td>
                <td className="py-2 pr-4 text-xs text-muted-foreground">
                  {sampleValues(mapping.sampleRows, i).join(', ') || '(empty)'}
                </td>
                <td className="py-2 pr-4">
                  {canEdit ? (
                    <div className="flex items-center gap-2">
                      <Select
                        aria-label={`Field for ${c.source}`}
                        value={c.field}
                        onChange={(e) => update(i, { field: e.target.value as MappedField })}
                        disabled={busy}
                      >
                        {FIELD_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </Select>
                      {c.field === 'quantity' && (
                        <input
                          aria-label={`Site for ${c.source}`}
                          placeholder="Site"
                          maxLength={64}
                          value={c.site ?? ''}
                          onChange={(e) => update(i, { site: e.target.value || null })}
                          className="w-20 rounded-md border border-border px-2 py-1 text-xs"
                        />
                      )}
                    </div>
                  ) : (
                    <span>
                      {fieldLabel(c.field)}
                      {c.field === 'quantity' && c.site ? ` (site ${c.site})` : ''}
                    </span>
                  )}
                </td>
                <td className="py-2">
                  {c.confidence && (
                    <StatusPill tone={c.confidence === 'high' ? 'green' : c.confidence === 'medium' ? 'amber' : 'neutral'}>
                      {c.confidence}
                    </StatusPill>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dupes.length > 0 && (
        <p role="alert" className="text-sm text-status-red">
          Each field can match one column only. Used twice: {dupes.map(fieldLabel).join(', ')}.
        </p>
      )}

      {canEdit ? (
        <div className="flex justify-end">
          <Button onClick={() => onConfirm(columns)} disabled={busy || dupes.length > 0}>
            {busy ? 'Working...' : 'Confirm mapping'}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">An org admin needs to confirm this mapping.</p>
      )}
    </Card>
  );
}
